import { createContext, type PropsWithChildren, use, useCallback, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import type { ConnectionState, PresenceAction, PresenceMessage } from "ably";
import { useAbly } from "ably/react";
import { deepEqual } from "fast-equals";
import { useSessionStorage } from "usehooks-ts";
import { AblyConnectionState } from "@remi/enums/ably-connection-state";
import { getError, RemiErrorDeprecated } from "@remi/error";
import { remiLogger } from "@remi/logging";
import { useEffect } from "@remi/next-ui";
import { createId } from "@remi/ts-utils";
import {
  type PresenceData,
  type PresenceEntityType,
  PresenceEntityTypeValues,
  type PresenceKey,
  type PresenceMap,
} from "@remi/types/socket/presence";

import { isAblyConnectionStateActive } from "~/utils/ablyConnectionState";
import { isAblyChannelLimitError, isAblyNetworkUnreachableError, isSuppressibleAblyError } from "~/utils/ablyErrors";
import { useCreateQuoteTaskIdForRfq } from "~/hooks/useCreateQuoteTaskIdForRfq";
import {
  applyPresenceMemberUpdate,
  buildPresenceMapFromMembers,
  type EntityPresenceMembers,
  getPresenceChannelName,
  getPresenceKeysFromRoute,
  getRfqIdFromRoute,
  groupSerializedPresenceKeysByEntityType,
  parsePresenceData,
  parsePresenceKey,
  serializePresenceKey,
} from "./utils/presence";

type PresenceContextValue = {
  presenceMapByEntityType: Partial<Record<PresenceEntityType, PresenceMap>>;
  registerEntityPresence: (entityType: PresenceEntityType) => () => void;
};

type AblyConnectionStateChange = { current: ConnectionState; previous: ConnectionState };

type InboundPresenceMessage = Partial<Pick<PresenceMessage, "action" | "clientId" | "connectionId">> & {
  data?: unknown;
};

const PRESENCE_FULL_REFRESH_INTERVAL_MS = 60_000;
const PRESENCE_REMOVE_ACTIONS = new Set<PresenceAction>(["leave", "absent"]);

const getSerializedKeysForEntityTypeFromPresenceData = (data: unknown, entityType: PresenceEntityType): string[] => {
  const parsedData = parsePresenceData(data);
  if (!parsedData) {
    return [];
  }

  return [...new Set(parsedData.keys)]
    .map(parsePresenceKey)
    .filter((parsedKey): parsedKey is PresenceKey => Boolean(parsedKey && parsedKey.entityType === entityType))
    .map(serializePresenceKey)
    .sort();
};

export const PresenceContext = createContext<PresenceContextValue | null>(null);

export const PresenceProvider = ({ children }: PropsWithChildren) => {
  const ably = useAbly();
  const router = useRouter();
  const [tabId] = useSessionStorage<string>("remi-presence-tab-id", createId());
  const currentRfqId = useMemo(
    () => (router.isReady ? getRfqIdFromRoute(router.pathname, router.query) : null),
    [router.isReady, router.pathname, router.query],
  );
  const createQuoteTaskId = useCreateQuoteTaskIdForRfq(currentRfqId);
  const rfqToTaskMap = useMemo(
    () =>
      currentRfqId !== null ? new Map([[currentRfqId, createQuoteTaskId]]) : new Map<number, string | undefined>(),
    [currentRfqId, createQuoteTaskId],
  );
  const [connectionState, setConnectionState] = useState<ConnectionState>(ably.connection.state);
  const isConnected = connectionState === AblyConnectionState.connected;
  const [presenceMembersByEntityType, setPresenceMembersByEntityType] = useState<
    Partial<Record<PresenceEntityType, EntityPresenceMembers>>
  >({});
  const presenceMapByEntityType = useMemo(() => {
    const result: Partial<Record<PresenceEntityType, PresenceMap>> = {};
    for (const entityType of PresenceEntityTypeValues) {
      const members = presenceMembersByEntityType[entityType];
      if (members) {
        result[entityType] = buildPresenceMapFromMembers(members);
      }
    }
    return result;
  }, [presenceMembersByEntityType]);
  const [activeEntityPresenceListenerVersion, setActiveEntityPresenceListenerVersion] = useState(0);
  const entityPresenceListenerCountRef = useRef<Partial<Record<PresenceEntityType, number>>>({});
  const publishedSerializedKeysByEntityTypeRef = useRef<Partial<Record<PresenceEntityType, string[]>>>({});

  const presenceKeys = useMemo(() => {
    if (!router.isReady) {
      return null;
    }
    return getPresenceKeysFromRoute(router.pathname, router.query, rfqToTaskMap);
  }, [router.isReady, router.pathname, router.query, rfqToTaskMap]);

  const setEntityPresenceMembers = useCallback((entityType: PresenceEntityType, members: EntityPresenceMembers) => {
    setPresenceMembersByEntityType((previous) => {
      const previousMembers = previous[entityType] ?? {};
      if (deepEqual(previousMembers, members)) {
        return previous;
      }
      return { ...previous, [entityType]: members };
    });
  }, []);

  const registerEntityPresence = useCallback((entityType: PresenceEntityType) => {
    entityPresenceListenerCountRef.current[entityType] = (entityPresenceListenerCountRef.current[entityType] ?? 0) + 1;
    setActiveEntityPresenceListenerVersion((previous) => previous + 1);

    return () => {
      const nextCount = Math.max((entityPresenceListenerCountRef.current[entityType] ?? 1) - 1, 0);
      entityPresenceListenerCountRef.current[entityType] = nextCount;
      setActiveEntityPresenceListenerVersion((previous) => previous + 1);

      if (nextCount > 0) {
        return;
      }

      setPresenceMembersByEntityType((previous) => {
        if (!(entityType in previous)) {
          return previous;
        }
        const next = { ...previous };
        delete next[entityType];
        return next;
      });
    };
  }, []);

  const getActiveEntityTypes = useCallback(() => {
    return PresenceEntityTypeValues.filter(
      (entityType) => (entityPresenceListenerCountRef.current[entityType] ?? 0) > 0,
    );
  }, []);

  const refreshEntityPresenceMap = useCallback(
    async (entityType: PresenceEntityType) => {
      const channelName = getPresenceChannelName(entityType);
      try {
        const currentConnectionState = ably.connection.state;
        if (!isAblyConnectionStateActive(currentConnectionState)) {
          remiLogger.captureCounterMetric("ably.channel.operation-skipped-connection-closed", 1, {
            attributes: { channel: channelName, connectionState: currentConnectionState },
          });
          return;
        }

        const channel = ably.channels.get(channelName);
        // waitForSync: false returns cached local presence instead of throwing 91005 when the channel is SUSPENDED (ODD-9033)
        const presenceSet = await channel.presence.get({ waitForSync: false });
        const nextMembers = presenceSet.reduce<EntityPresenceMembers>((acc, member) => {
          const { connectionId, clientId } = member;
          if (!connectionId || !clientId) {
            return acc;
          }

          const serializedKeys = getSerializedKeysForEntityTypeFromPresenceData(member.data, entityType);
          if (serializedKeys.length === 0) {
            return acc;
          }

          acc[connectionId] = { clientId, serializedKeys };
          return acc;
        }, {});

        setEntityPresenceMembers(entityType, nextMembers);
      } catch (error: unknown) {
        if (isSuppressibleAblyError(error)) {
          return;
        }
        remiLogger.captureError(getError(error));
      }
    },
    [ably, setEntityPresenceMembers],
  );

  const updateEntityTypePresenceStatus = useCallback(
    async (entityType: PresenceEntityType, serializedKeys: string[]) => {
      const channelName = getPresenceChannelName(entityType);
      const operation = serializedKeys.length > 0 ? "update" : "leave";
      try {
        const currentConnectionState = ably.connection.state;
        if (!isAblyConnectionStateActive(currentConnectionState)) {
          remiLogger.captureCounterMetric("ably.channel.operation-skipped-connection-closed", 1, {
            attributes: { channel: channelName, connectionState: currentConnectionState, operation },
          });
          return false;
        }

        const channel = ably.channels.get(channelName);
        if (channel.state !== "attached") {
          await channel.attach();
        }
        if (serializedKeys.length > 0) {
          const data: PresenceData = { keys: serializedKeys, tabId };
          await channel.presence.update(data);
        } else {
          await channel.presence.leave();
        }

        remiLogger.captureCounterMetric("ably.presence.update-success", 1, {
          attributes: { channel: channelName, connectionState: currentConnectionState, operation },
        });
        return true;
      } catch (error: unknown) {
        const err = getError(error);

        if (isAblyChannelLimitError(err)) {
          remiLogger.captureCounterMetric("ably.presence.limit-exceeded", 1, {
            attributes: { channel: channelName, operation },
          });
        } else if (isAblyNetworkUnreachableError(err)) {
          remiLogger.captureCounterMetric("ably.connection.network-unreachable", 1, {
            attributes: { channel: channelName, operation },
          });
        } else if (isSuppressibleAblyError(err)) {
          // suppress noisy Ably attach timeout/channel limit errors
        } else {
          remiLogger.captureCounterMetric("ably.presence.update-failure", 1, {
            attributes: { channel: channelName, operation },
          });
        }
        return false;
      }
    },
    [ably, tabId],
  );

  const detachEntityTypeChannelIfUnused = useCallback(
    async (
      entityType: PresenceEntityType,
      publishedSerializedKeysByEntityType?: Partial<Record<PresenceEntityType, string[]>>,
    ) => {
      const listenerCount = entityPresenceListenerCountRef.current[entityType] ?? 0;
      const publishedSerializedKeys =
        publishedSerializedKeysByEntityType?.[entityType] ??
        publishedSerializedKeysByEntityTypeRef.current[entityType] ??
        [];
      if (listenerCount > 0 || publishedSerializedKeys.length > 0) {
        return;
      }

      const channelName = getPresenceChannelName(entityType);
      try {
        const currentConnectionState = ably.connection.state;
        if (!isAblyConnectionStateActive(currentConnectionState)) {
          remiLogger.captureCounterMetric("ably.channel.operation-skipped-connection-closed", 1, {
            attributes: { channel: channelName, connectionState: currentConnectionState, operation: "detach" },
          });
          return;
        }

        const channel = ably.channels.get(channelName);
        if (channel.state !== "initialized" && channel.state !== "detached" && channel.state !== "failed") {
          await channel.detach();
        }
        if (channel.state === "initialized" || channel.state === "detached" || channel.state === "failed") {
          ably.channels.release(channelName);
        }
      } catch (error: unknown) {
        remiLogger.captureCounterMetric("ably.presence.update-failure", 1, {
          attributes: { channel: channelName },
        });
        if (isSuppressibleAblyError(error)) {
          return;
        }
        remiLogger.captureError(getError(error));
      }
    },
    [ably],
  );

  const setPresenceKeys = useCallback(
    async (keys: PresenceKey[] | null) => {
      const desiredSerializedKeysByEntityType = groupSerializedPresenceKeysByEntityType(keys);

      if (!isConnected) {
        return;
      }

      const publishedSerializedKeysByEntityType = publishedSerializedKeysByEntityTypeRef.current;
      const entityTypesWithPossibleDiff = PresenceEntityTypeValues.filter((entityType) => {
        const desiredKeys = desiredSerializedKeysByEntityType[entityType] ?? [];
        const publishedKeys = publishedSerializedKeysByEntityType[entityType] ?? [];
        return desiredKeys.length > 0 || publishedKeys.length > 0;
      });

      const entityTypesToUpdate = entityTypesWithPossibleDiff.filter((entityType) => {
        const desiredKeys = desiredSerializedKeysByEntityType[entityType] ?? [];
        const publishedKeys = publishedSerializedKeysByEntityType[entityType] ?? [];
        return !deepEqual(desiredKeys, publishedKeys);
      });

      if (entityTypesToUpdate.length === 0) {
        return;
      }

      const updateResults = await Promise.all(
        entityTypesToUpdate.map(async (entityType) => {
          const serializedKeys = desiredSerializedKeysByEntityType[entityType] ?? [];
          const updated = await updateEntityTypePresenceStatus(entityType, serializedKeys);
          return { entityType, serializedKeys, updated };
        }),
      );

      const nextPublishedSerializedKeysByEntityType = { ...publishedSerializedKeysByEntityType };
      for (const result of updateResults) {
        if (!result.updated) {
          continue;
        }
        if (result.serializedKeys.length === 0) {
          delete nextPublishedSerializedKeysByEntityType[result.entityType];
        } else {
          nextPublishedSerializedKeysByEntityType[result.entityType] = result.serializedKeys;
        }
      }
      publishedSerializedKeysByEntityTypeRef.current = nextPublishedSerializedKeysByEntityType;
      await Promise.all(
        PresenceEntityTypeValues.map(async (entityType) => {
          await detachEntityTypeChannelIfUnused(entityType, nextPublishedSerializedKeysByEntityType);
        }),
      );
    },
    [detachEntityTypeChannelIfUnused, isConnected, updateEntityTypePresenceStatus],
  );

  const applyEntityPresenceMessage = useCallback((entityType: PresenceEntityType, message: InboundPresenceMessage) => {
    const { connectionId, clientId } = message;
    if (!connectionId || !clientId) {
      return;
    }

    const serializedKeys = PRESENCE_REMOVE_ACTIONS.has(message.action ?? "update")
      ? []
      : getSerializedKeysForEntityTypeFromPresenceData(message.data, entityType);
    setPresenceMembersByEntityType((previous) => {
      const previousMembers = previous[entityType] ?? {};
      const nextMembers = applyPresenceMemberUpdate(previousMembers, connectionId, clientId, serializedKeys);
      if (nextMembers === previousMembers) {
        return previous;
      }
      return { ...previous, [entityType]: nextMembers };
    });
  }, []);

  useEffect(() => {
    setConnectionState(ably.connection.state);

    const handleConnectionStateChange = (stateChange: AblyConnectionStateChange) => {
      setConnectionState(stateChange.current);

      if (!isAblyConnectionStateActive(stateChange.current)) {
        publishedSerializedKeysByEntityTypeRef.current = {};
      }
    };

    ably.connection.on(handleConnectionStateChange);
    return () => {
      ably.connection.off(handleConnectionStateChange);
    };
  }, [ably.connection]);

  useEffect(() => {
    if (!router.isReady || (typeof document !== "undefined" && document.hidden) || !isConnected) {
      return;
    }
    void setPresenceKeys(presenceKeys);
  }, [presenceKeys, setPresenceKeys, router.isReady, isConnected]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const activeEntityTypes = getActiveEntityTypes();
    if (activeEntityTypes.length === 0) {
      return;
    }

    let didUnmount = false;
    const subscriptions: Array<{
      channel: ReturnType<typeof ably.channels.get>;
      handlePresenceMessage: (message: InboundPresenceMessage) => void;
    }> = [];

    void Promise.all(
      activeEntityTypes.map(async (entityType) => {
        const channel = ably.channels.get(getPresenceChannelName(entityType));
        const handlePresenceMessage = (message: InboundPresenceMessage) => {
          applyEntityPresenceMessage(entityType, message);
        };

        await channel.presence.subscribe(handlePresenceMessage);
        if (didUnmount) {
          channel.presence.unsubscribe(handlePresenceMessage);
          return;
        }

        subscriptions.push({ channel, handlePresenceMessage });
        await refreshEntityPresenceMap(entityType);
      }),
    ).catch((error: unknown) => {
      if (isSuppressibleAblyError(error)) {
        return;
      }
      remiLogger.captureError(getError(error));
    });

    return () => {
      didUnmount = true;
      for (const subscription of subscriptions) {
        subscription.channel.presence.unsubscribe(subscription.handlePresenceMessage);
      }
    };
  }, [
    activeEntityPresenceListenerVersion,
    ably,
    applyEntityPresenceMessage,
    getActiveEntityTypes,
    isConnected,
    refreshEntityPresenceMap,
  ]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    void Promise.all(
      PresenceEntityTypeValues.map(async (entityType) => {
        await detachEntityTypeChannelIfUnused(entityType);
      }),
    );
  }, [activeEntityPresenceListenerVersion, detachEntityTypeChannelIfUnused, isConnected]);

  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const intervalId = window.setInterval(() => {
      const activeEntityTypes = getActiveEntityTypes();
      for (const entityType of activeEntityTypes) {
        void refreshEntityPresenceMap(entityType);
      }
    }, PRESENCE_FULL_REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [getActiveEntityTypes, isConnected, refreshEntityPresenceMap]);

  const value = useMemo(
    () => ({
      presenceMapByEntityType,
      registerEntityPresence,
    }),
    [presenceMapByEntityType, registerEntityPresence],
  );

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
};

type EntityPresenceProps = {
  entityType: PresenceEntityType;
};

export const EntityPresence = ({ entityType }: EntityPresenceProps) => {
  const presenceContext = use(PresenceContext);
  if (presenceContext === null) {
    throw new RemiErrorDeprecated({ message: "EntityPresence may only be called within <PresenceProvider />" });
  }
  const { registerEntityPresence } = presenceContext;

  useEffect(() => {
    const unregister = registerEntityPresence(entityType);
    return unregister;
  }, [entityType, registerEntityPresence]);

  return null;
};
